// The platform calls this page makes, typed from autrik_sdk's specs
// (spec/identity/spec.yml for service-token). Cookies carry the Kratos
// session (credentials: 'include'), as the Flutter page's Dio did.

import type { Config } from './config';

export interface Session {
  identityId: string;
  email: string;
  workspaceId: string; // metadata_public.company_id, else traits.companyid
  role: string; // metadata_public.user_role, else traits.role; 'admin' may register devices
}

export class ApiError extends Error {
  constructor(
    readonly call: string,
    readonly status: number,
    readonly body: string,
  ) {
    super(`${call}: HTTP ${status} ${body.slice(0, 200)}`);
    this.name = 'ApiError';
  }
}

async function call(fetchFn: typeof fetch, name: string, url: string, init: RequestInit = {}): Promise<unknown> {
  const res = await fetchFn(url, {
    credentials: 'include',
    ...init,
    headers: { Accept: 'application/json', ...(init.headers ?? {}) },
  });
  const text = await res.text();
  if (!res.ok) throw new ApiError(name, res.status, text);
  return text ? JSON.parse(text) : null;
}

/** The current Kratos session, or null when there is none (401). */
export async function whoami(cfg: Config, fetchFn: typeof fetch = fetch): Promise<Session | null> {
  let s: {
    identity?: {
      id?: string;
      traits?: Record<string, unknown>;
      metadata_public?: Record<string, unknown> | null;
    };
  };
  try {
    s = (await call(fetchFn, 'whoami', `${cfg.kratosPublic}/sessions/whoami`)) as typeof s;
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
  const id = s.identity;
  if (!id?.id) return null;
  const traits = id.traits ?? {};
  const meta = id.metadata_public;
  const workspaceId = meta ? String(meta['company_id'] ?? '') : String(traits['companyid'] ?? '');
  const role = meta ? String(meta['user_role'] ?? '') : String(traits['role'] ?? '');
  return { identityId: id.id, email: String(traits['email'] ?? ''), workspaceId, role };
}

type ServiceTokenRequest =
  | { service: 'emqx'; principal_kind: 'djirc' }
  | { service: 'livekit'; token_type: 'publisher'; room_name: '' };

async function serviceToken(cfg: Config, body: ServiceTokenRequest, fetchFn: typeof fetch): Promise<string> {
  const r = (await call(fetchFn, `service-token ${body.service}`, `${cfg.backend}/identity/service-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })) as { token?: string } | null;
  if (!r?.token) throw new ApiError(`service-token ${body.service}`, 200, 'no token');
  return r.token;
}

/** EMQX JWT for the RC: principal_kind djirc (iam rejects other kinds for this page). */
export function emqxToken(cfg: Config, fetchFn: typeof fetch = fetch): Promise<string> {
  return serviceToken(cfg, { service: 'emqx', principal_kind: 'djirc' }, fetchFn);
}

/** LiveKit publisher token with no room name: iam grants org-<workspace> (T37 F15). */
export function livekitToken(cfg: Config, fetchFn: typeof fetch = fetch): Promise<string> {
  return serviceToken(cfg, { service: 'livekit', token_type: 'publisher', room_name: '' }, fetchFn);
}

// Devices (fleet-api through the gateway; autrik_sdk spec/skymind/spec.yml,
// /devices/workspaces/{workspace_id}).

export interface Device {
  device_id?: string;
  device_sn?: string;
  device_name?: string;
  sdk?: string;
  stream_id?: string;
  domain?: number;
  device_type?: number;
  sub_type?: number;
}

export interface CreateDeviceRequest {
  device_id: string;
  device_sn: string;
  device_name: string;
  sdk: string;
  stream_id: string;
  domain: number;
  device_type?: number; // left out until Pilot's update_topo names the model
  sub_type?: number;
  is_sitl: boolean;
}

const devicesUrl = (cfg: Config, workspaceId: string) =>
  `${cfg.backend}/devices/workspaces/${encodeURIComponent(workspaceId)}`;

export async function listDevices(cfg: Config, workspaceId: string, fetchFn: typeof fetch = fetch): Promise<Device[]> {
  const r = (await call(fetchFn, 'fetchDevices', devicesUrl(cfg, workspaceId))) as { devices?: Device[] | null } | null;
  return r?.devices ?? [];
}

export async function createDevice(
  cfg: Config,
  workspaceId: string,
  body: CreateDeviceRequest,
  fetchFn: typeof fetch = fetch,
): Promise<void> {
  await call(fetchFn, 'createDevice', devicesUrl(cfg, workspaceId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** The SDK migration: MOBILESDK (the AutrikOS DJI app) → DJI_CLOUDAPI (Pilot 2). */
export async function setDeviceSdk(
  cfg: Config,
  workspaceId: string,
  deviceId: string,
  sdk: string,
  fetchFn: typeof fetch = fetch,
): Promise<void> {
  await call(fetchFn, 'updateDevice', `${devicesUrl(cfg, workspaceId)}?device_id=${encodeURIComponent(deviceId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sdk }),
  });
}
